import { useState } from 'react';
import type { FormEvent } from 'react';

import type { NewApiEndpoint, OperatorOption } from '../../api/monitor';
import { SearchSelect } from '../../components/ui/SearchSelect';

interface NewApiFormProps {
  operators: OperatorOption[];
  saving: boolean;
  onSubmit: (payload: NewApiEndpoint) => Promise<void>;
}

const FIELD_CLASS =
  'border-line-300 focus:border-navy-800 focus:outline-navy-800/20 rounded-[10px] border px-3 py-[11px] text-[13.5px] outline-2 outline-transparent';

/** Cadastro de uma nova API monitorada. */
export function NewApiForm({ operators, saving, onSubmit }: NewApiFormProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [operatorCode, setOperatorCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!name.trim() || !url.trim()) {
      setError('Informe o nome e a URL da API.');
      return;
    }

    try {
      await onSubmit({
        name: name.trim(),
        url: url.trim(),
        operatorCode: operatorCode || null,
      });

      setName('');
      setUrl('');
      setOperatorCode('');
      setError(null);
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border-line-200 mt-3.5 rounded-2xl border bg-white p-4"
    >
      <div className="text-[14px] font-extrabold">Adicionar API</div>
      <p className="text-ink-600 mt-0.5 mb-2.5 text-[12.5px]">
        A verificação começa no próximo ciclo do cron — ou agora, no botão “Verificar agora”.
      </p>

      <div className="flex flex-wrap gap-2">
        <input
          name="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Nome (ex.: Portal Claro GOL)"
          maxLength={120}
          className={`${FIELD_CLASS} min-w-[180px] flex-1`}
        />

        {/* O vazio aqui não é "nada escolhido", é a escolha padrão (API
            interna); por isso a opção existe de verdade em vez de ficar só
            como placeholder apagado. */}
        <SearchSelect
          value={operatorCode}
          onChange={setOperatorCode}
          options={[
            { value: '', label: 'Interna (SICOP)' },
            ...operators.map((operator) => ({ value: operator.code, label: operator.name })),
          ]}
          searchPlaceholder="Buscar operadora…"
          aria-label="Operadora da API"
          className="w-auto min-w-[200px]"
        />

        <input
          name="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://…"
          inputMode="url"
          maxLength={500}
          className={`${FIELD_CLASS} min-w-[220px] flex-[2]`}
        />

        <button
          type="submit"
          disabled={saving}
          className="bg-navy-800 hover:bg-navy-950 rounded-[10px] px-[18px] py-[11px] text-[13px] font-extrabold text-white disabled:opacity-60"
        >
          {saving ? 'Adicionando…' : 'Adicionar'}
        </button>
      </div>

      {error ? (
        <div className="text-danger-600 mt-2.5 text-[12.5px] font-bold">{error}</div>
      ) : null}
    </form>
  );
}
